// apps/client/src/components/AIChat/useAIChat.ts

import { useState, useCallback, useRef, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import {
  Message,
  AskCommissionQuestionRequest,
  AskCommissionQuestionResponse,
  ChatError,
} from './types';

/**
 * Custom hook for managing AI chat state and interactions
 * Handles Firebase Cloud Functions communication and message history
 */
export function useAIChat() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [conversationId, setConversationId] = useState<string>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ChatError | null>(null);

  // Ref to track the latest message for retry
  const lastQuestionRef = useRef<string>('');

  /**
   * Send a question to the AI and handle the response
   */
  const sendMessage = useCallback(
    async (question: string) => {
      // Store for potential retry
      lastQuestionRef.current = question;

      // Add user message to chat
      const userMessage: Message = {
        id: `user-${Date.now()}`,
        role: 'user',
        content: question,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMessage]);
      setIsLoading(true);
      setError(null);

      try {
        // Call Cloud Function with extended timeout (3 minutes)
        // Default is 70s but AI queries can take 90-120 seconds
        const askQuestion = httpsCallable<
          AskCommissionQuestionRequest,
          AskCommissionQuestionResponse
        >(functions, 'askCommissionQuestion', { timeout: 180000 });

        const result = await askQuestion({
          question,
          conversationId,
        });

        // Add AI response to chat
        const aiMessage: Message = {
          id: result.data.queryId,
          role: 'assistant',
          content: result.data.answer,
          insights: result.data.insights,
          visualizations: result.data.visualizations,
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, aiMessage]);

        // Update conversation ID for context
        setConversationId(result.data.conversationId);
      } catch (err: any) {
        console.error('AI chat error:', err);

        // Determine error type and user-friendly message
        const isTimeout =
          err.code === 'deadline-exceeded' ||
          err.message?.includes('deadline') ||
          err.message?.includes('timeout');
        const isRateLimit =
          err.code === 'resource-exhausted' ||
          err.message?.includes('rate limit');
        const isPermission = err.code === 'permission-denied';

        // Parse error with better context
        const chatError: ChatError = {
          code: err.code || 'unknown',
          message: err.message || 'Failed to get response from AI',
          retryable: !isPermission,
        };
        setError(chatError);

        // User-friendly error messages based on error type
        let errorContent: string;
        if (isTimeout) {
          errorContent =
            "That query took longer than expected - our AI is processing a lot of data! Please tap 'Retry' to try again. Complex questions may take a moment.";
        } else if (isRateLimit) {
          errorContent =
            "You've reached your hourly query limit. Take a short break and your queries will reset soon!";
        } else if (isPermission) {
          errorContent =
            "I don't have permission to access your data. Please contact support.";
        } else {
          errorContent =
            "Something went wrong processing your question. Please tap 'Retry' to try again.";
        }

        // Add error message to chat
        const errorMessage: Message = {
          id: `error-${Date.now()}`,
          role: 'assistant',
          content: errorContent,
          timestamp: new Date(),
          error: true,
        };
        setMessages((prev) => [...prev, errorMessage]);
      } finally {
        setIsLoading(false);
      }
    },
    [conversationId]
  );

  /**
   * Retry the last failed message
   */
  const retryLastMessage = useCallback(() => {
    if (lastQuestionRef.current) {
      sendMessage(lastQuestionRef.current);
    }
  }, [sendMessage]);

  /**
   * Clear conversation history
   */
  const clearHistory = useCallback(() => {
    setMessages([]);
    setConversationId(undefined);
    setError(null);
    lastQuestionRef.current = '';
  }, []);

  /**
   * Add welcome message on mount
   */
  useEffect(() => {
    if (messages.length === 0) {
      const welcomeMessage: Message = {
        id: 'welcome',
        role: 'assistant',
        content:
          "Hi! I'm your VALUTTEN AI assistant. I can help you understand your commission data, analyze trends, and answer questions about your performance. What would you like to know?",
        timestamp: new Date(),
      };
      setMessages([welcomeMessage]);
    }
  }, []);

  return {
    messages,
    conversationId,
    isLoading,
    error,
    sendMessage,
    retryLastMessage,
    clearHistory,
  };
}
